import {
  MOCK_ADMIN,
  MOCK_PRODUCT,
  MOCK_STAKEHOLDER,
  createSeedRequests,
  createSeedUsers,
  mockDevelopmentFor,
} from "@/lib/mock/data";
import { getAccessToken } from "@/lib/auth-storage";
import { DIRECTORATES, PROJECT_OBJECTIVES } from "@/lib/constants";
import type {
  CreateFeatureRequestInput,
  DevelopmentStatus,
  FeatureRequest,
  FeatureRequestStatus,
  FeatureRequestUser,
  ManagedUser,
  PMNote,
  UpdateFeatureRequestStatusInput,
  User,
  UserRole,
} from "@/types";

const MOCK_TOKEN_PREFIX = "mock:";

let requests = createSeedRequests();
let mockUsers = createSeedUsers();
let nextRequestId = Math.max(...requests.map((r) => r.id), 0) + 1;
let nextEventId =
  Math.max(
    0,
    ...requests.flatMap((r) => (r.events ?? []).map((e) => e.id)),
  ) + 1;
let nextNoteId =
  Math.max(
    0,
    ...requests.flatMap((r) => (r.pm_notes ?? []).map((n) => n.id)),
  ) + 1;

function delay(ms = 280): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

export function isMockToken(token: string | null | undefined): boolean {
  return Boolean(token?.startsWith(MOCK_TOKEN_PREFIX));
}

export function mockTokenForRole(role: UserRole): string {
  return `${MOCK_TOKEN_PREFIX}${role}`;
}

export function userFromMockToken(token: string | null | undefined): User | null {
  if (!token?.startsWith(MOCK_TOKEN_PREFIX)) return null;
  const role = token.slice(MOCK_TOKEN_PREFIX.length);
  if (role === "admin") return { ...(mockUsers.find((u) => u.role === "admin") ?? MOCK_ADMIN) };
  if (role === "product") return { ...(mockUsers.find((u) => u.id === MOCK_PRODUCT.id) ?? MOCK_PRODUCT) };
  if (role === "stakeholder") return { ...(mockUsers.find((u) => u.id === MOCK_STAKEHOLDER.id) ?? MOCK_STAKEHOLDER) };
  return null;
}

function currentUser(): User {
  return userFromMockToken(getAccessToken()) ?? MOCK_STAKEHOLDER;
}

function cloneRequest(fr: FeatureRequest): FeatureRequest {
  return structuredClone(fr);
}

function assertCanEditRequest(fr: FeatureRequest, user: User): void {
  if (user.role === "stakeholder" && fr.requester_id !== user.id) {
    throw Object.assign(new Error("forbidden"), { status: 403 });
  }
  if (fr.status === "approved" || fr.status === "rejected") {
    throw Object.assign(new Error("request can no longer be edited after Proceed or Decline"), { status: 409 });
  }
}

function localTodayISO(): string {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

/** Active queue member: approved and not yet released. */
function isQueueActive(fr: FeatureRequest): boolean {
  return fr.status === "approved" && !fr.actual_delivery && fr.development_status !== "Done" && fr.queue_position != null;
}

/** Convert stored queue positions into 1-based ranks among active members. */
function withQueueRanks(rows: FeatureRequest[]): FeatureRequest[] {
  const active = rows.filter(isQueueActive).sort((a, b) => (a.queue_position ?? 0) - (b.queue_position ?? 0));
  const rank = new Map(active.map((fr, i) => [fr.id, i + 1]));
  return rows.map((fr) => ({ ...fr, queue_position: rank.get(fr.id) ?? null }));
}

export async function mockGetMe(): Promise<User> {
  await delay();
  const user = currentUser();
  if (!user.is_active) {
    throw new Error("account is disabled");
  }
  return { ...user };
}

export async function mockGetLoginMethods() {
  await delay(80);
  return {
    sso_enabled: false,
    local_login_enabled: false,
    nexus_login_url: "",
  };
}

export async function mockListFeatureRequests(
  status?: FeatureRequestStatus | "all",
): Promise<FeatureRequest[]> {
  await delay();
  // Shared board: all roles see every request; UI "Submitted by me" filters client-side.
  let rows = withQueueRanks(requests.map(cloneRequest));
  if (status && status !== "all") {
    rows = rows.filter((r) => r.status === status);
  }
  return rows.sort(
    (a, b) =>
      new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
  );
}

export async function mockGetFeatureRequest(
  id: number | string,
): Promise<FeatureRequest> {
  await delay();
  const fr = requests.find((r) => r.id === Number(id));
  if (!fr) {
    throw Object.assign(new Error("feature request not found"), { status: 404 });
  }
  // Rank against the whole queue, not just this request.
  return withQueueRanks(requests.map(cloneRequest)).find((r) => r.id === fr.id) ?? cloneRequest(fr);
}

export async function mockDeleteFeatureRequest(id: number | string): Promise<void> {
  await delay();
  const user = currentUser();
  if (user.role !== "admin") {
    throw Object.assign(new Error("forbidden"), { status: 403 });
  }
  const numId = Number(id);
  const idx = requests.findIndex((r) => r.id === numId);
  if (idx < 0) {
    throw Object.assign(new Error("feature request not found"), { status: 404 });
  }
  requests = requests.filter((r) => r.id !== numId);
  const active = requests
    .filter(isQueueActive)
    .sort((a, b) => (a.queue_position ?? 0) - (b.queue_position ?? 0));
  const position = new Map(active.map((r, i) => [r.id, i + 1]));
  requests = requests.map((fr) =>
    fr.queue_position == null
      ? fr
      : { ...fr, queue_position: position.get(fr.id) ?? null },
  );
}

/** Admin-only: relink an approved request to another Jira Project key. */
export async function mockUpdateJiraProjectLink(
  id: number | string,
  jiraIssueKey: string,
): Promise<FeatureRequest> {
  await delay();
  const user = currentUser();
  if (user.role !== "admin") {
    throw Object.assign(new Error("forbidden"), { status: 403 });
  }
  const fr = requests.find((r) => r.id === Number(id));
  if (!fr) {
    throw Object.assign(new Error("feature request not found"), { status: 404 });
  }
  if (fr.status !== "approved") {
    throw Object.assign(new Error("invalid status transition"), { status: 400 });
  }
  let key = jiraIssueKey.trim();
  const browse = "https://lionparcel.atlassian.net/browse/";
  if (key.toLowerCase().startsWith(browse)) {
    key = key.slice(browse.length);
  }
  key = key.toUpperCase().trim();
  if (!/^[A-Z][A-Z0-9]+-\d+$/.test(key)) {
    throw Object.assign(
      new Error("jira_issue_key must be a valid Jira Project issue key (e.g. GENESIS-24575)"),
      { status: 400 },
    );
  }
  const prev = (fr.jira_issue_key || "").trim();
  if (prev.toUpperCase() === key) {
    return cloneRequest(fr);
  }
  fr.jira_issue_key = key;
  fr.jira_issue_url = `${browse}${key}`;
  fr.updated_at = new Date().toISOString();
  fr.events = [
    ...(fr.events ?? []),
    {
      id: nextEventId++,
      actor_id: user.id,
      actor: asRequestUser(user),
      from_status: "",
      to_status: "jira_project",
      note: prev ? `${prev} → ${key}` : key,
      created_at: new Date().toISOString(),
    },
  ];
  return cloneRequest(fr);
}

/** Product/Admin: create Jira Project for an approved request that has no link yet. */
export async function mockCreateJiraProject(
  id: number | string,
  jiraProjectKey?: string,
): Promise<FeatureRequest> {
  await delay();
  const user = currentUser();
  if (user.role !== "product" && user.role !== "admin") {
    throw Object.assign(new Error("forbidden"), { status: 403 });
  }
  const fr = requests.find((r) => r.id === Number(id));
  if (!fr) {
    throw Object.assign(new Error("feature request not found"), { status: 404 });
  }
  if (fr.status !== "approved") {
    throw Object.assign(new Error("invalid status transition"), { status: 400 });
  }
  if (fr.jira_issue_key?.trim()) {
    throw Object.assign(new Error("request already has a linked Jira Project"), { status: 409 });
  }
  const board = (jiraProjectKey?.trim() || "GENESIS").toUpperCase();
  const key = `${board}-${4000 + fr.id}`;
  fr.jira_issue_key = key;
  fr.jira_issue_url = `https://lionparcel.atlassian.net/browse/${key}`;
  fr.development_status = "Backlog";
  fr.updated_at = new Date().toISOString();
  fr.events = [
    ...(fr.events ?? []),
    {
      id: nextEventId++,
      actor_id: user.id,
      actor: asRequestUser(user),
      from_status: "",
      to_status: "jira_project",
      note: `Created ${key}`,
      created_at: new Date().toISOString(),
    },
  ];
  return cloneRequest(fr);
}

export async function mockCreateFeatureRequest(
  input: CreateFeatureRequestInput,
): Promise<FeatureRequest> {
  await delay();
  if (!(PROJECT_OBJECTIVES as readonly string[]).includes(input.project_objective?.trim() ?? "")) {
    throw Object.assign(new Error("project_objective must be one of the configured strategic pillars"), { status: 400 });
  }
  if (!(DIRECTORATES as readonly string[]).includes(input.directorate?.trim() ?? "")) {
    throw Object.assign(new Error("directorate must be one of the configured directorates"), { status: 400 });
  }
  if (!input.expected_deadline?.trim()) {
    throw Object.assign(new Error("expected_deadline is required"), { status: 400 });
  }
  if (input.expected_deadline?.trim()) {
    const today = new Date();
    const local = new Date(today.getTime() - today.getTimezoneOffset() * 60_000);
    if (input.expected_deadline.trim() < local.toISOString().slice(0, 10)) {
      throw Object.assign(new Error("expected_deadline cannot be in the past"), { status: 400 });
    }
  }
  const user = currentUser();
  const now = new Date().toISOString();
  const requester = {
    id: user.id,
    username: user.username,
    display_name: user.display_name,
    role: user.role,
  };
  const fr: FeatureRequest = {
    id: nextRequestId++,
    title: input.title.trim(),
    description: input.description?.trim() ?? "",
    business_justification: input.business_justification?.trim() ?? "",
    important_links: (input.important_links ?? []).map((l) => l.trim()).filter(Boolean),
    project_objective: input.project_objective?.trim() || null,
    priority: input.priority?.trim() || null,
    directorate: input.directorate?.trim() || null,
    ceo_highlight: Boolean(input.ceo_highlight),
    ceo_note: input.ceo_highlight ? input.ceo_note?.trim() || null : null,
    adhoc: false,
    pm_notes: [],
    expected_deadline: input.expected_deadline?.trim() || null,
    requester_id: user.id,
    requester,
    status: "submitted",
    events: [
      {
        id: nextEventId++,
        actor_id: user.id,
        actor: requester,
        from_status: "",
        to_status: "submitted",
        note: "Feature request submitted",
        created_at: now,
      },
    ],
    created_at: now,
    updated_at: now,
  };
  requests = [fr, ...requests];
  return cloneRequest(fr);
}

export async function mockUpdateExpectedDeadline(
  id: number | string,
  expectedDeadline: string,
): Promise<FeatureRequest> {
  await delay();
  const user = currentUser();
  const idx = requests.findIndex((r) => r.id === Number(id));
  if (idx < 0) {
    throw Object.assign(new Error("feature request not found"), { status: 404 });
  }
  assertCanEditRequest(requests[idx], user);
  const value = expectedDeadline.trim();
  if (!value) {
    throw Object.assign(new Error("expected_deadline is required"), { status: 400 });
  }
  if (value < localTodayISO()) {
    throw Object.assign(new Error("expected_deadline cannot be in the past"), { status: 400 });
  }
  const updated: FeatureRequest = {
    ...requests[idx],
    expected_deadline: value,
    updated_at: new Date().toISOString(),
  };
  requests[idx] = updated;
  return cloneRequest(updated);
}

const mockBRDFiles = new Map<number, File>();

export async function mockUploadBRD(id: number | string, file: File): Promise<FeatureRequest> {
  await delay();
  const ext = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
  if (ext !== ".pdf") {
    throw Object.assign(new Error("brd document must be a PDF file"), { status: 400 });
  }
  if (file.size > 20 * 1024 * 1024) {
    throw Object.assign(new Error("brd document exceeds the maximum upload size"), { status: 400 });
  }
  const fr = await mockGetFeatureRequest(id);
  const idx = requests.findIndex((r) => r.id === fr.id);
  assertCanEditRequest(requests[idx], currentUser());
  mockBRDFiles.set(fr.id, file);
  const updated: FeatureRequest = {
    ...fr,
    brd: {
      file_name: file.name,
      file_size: file.size,
      content_type: file.type || "application/octet-stream",
      download_url: `/api/feature-requests/${fr.id}/brd`,
    },
    updated_at: new Date().toISOString(),
  };
  requests[idx] = updated;
  return cloneRequest(updated);
}

const MOCK_BRD_TEXT = [
  "Business Requirements Document (BRD)",
  "1. Background & problem statement",
  "Consignees frequently miss the first delivery attempt because they are away when the driver arrives. Failed first attempts drive redelivery cost and support contacts.",
  "2. Objectives & success metrics",
  "Cut failed first delivery attempts from 12% to under 7% within two quarters by notifying the consignee 30 minutes before arrival with a live-map deep link.",
  "3. Scope",
  "In scope: push notifications for all retail shipments in Jabodetabek. Out of scope: marketplace shipments handled by partners.",
  "4. Business requirements",
  "4.1 Notify the consignee when the driver is 30 minutes away, with a deep link to live tracking. 4.2 Respect quiet hours and opt-out preferences.",
  "5. Stakeholders & PIC",
  "Requester: Customer Experience. PIC: Ayu Stakeholder. Approvers: CX Director, Operations Director.",
  "6. Timeline & expected go-live",
  "Target go-live ahead of the year-end peak season campaign.",
].join("\n\n");

export async function mockGetBRDContent(id: number | string): Promise<{ file_name: string; text: string; preview_available: boolean }> {
  await delay();
  const fr = await mockGetFeatureRequest(id);
  if (!fr.brd) {
    throw Object.assign(new Error("brd document not found"), { status: 404 });
  }
  if (fr.brd.file_name.toLowerCase().endsWith(".doc")) {
    return { file_name: fr.brd.file_name, text: "", preview_available: false };
  }
  return { file_name: fr.brd.file_name, text: MOCK_BRD_TEXT, preview_available: true };
}

export async function mockDownloadBRDTemplate(): Promise<void> {
  await delay(120);
  const blob = new Blob(
    ["Business Requirements Document (BRD)\n\nInitiative / project name\n[The name stakeholders will recognise.]\n\n1. Background & problem statement\n[What is happening today, who is affected, and what evidence shows this is worth solving now?]\n\n2. Objectives & success metrics\n[Metric, current value, target value.]\n\n3. Scope\nIn scope: [...]\nOut of scope: [...]\n\n4. Business requirements\n[List the functional requirements one by one.]\n\n5. Stakeholders & PIC\n[...]\n\n6. Timeline & expected go-live\n[...]\n\n7. Risks, assumptions & dependencies\n[...]\n\n8. CEO highlight\n[Why this matters at company level, in 2-3 sentences.]"],
    { type: "text/plain" },
  );
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "BRD Template - Open Backlog.docx";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function mockDownloadBRDExample(): Promise<void> {
  await delay(120);
  const blob = new Blob(
    ["Business Requirements Document (BRD) — Example\n\nInitiative / project name\nSame-day pickup for corporate accounts\n\nDirectorate\nCommercial\n\n1. Background & problem statement\nCorporate accounts currently wait until the next business day for pickup…\n\n2. Objectives & success metrics\n70% same-day fulfilment in Jabodetabek within 90 days.\n\n8. CEO highlight\nCloses a competitive gap on top corporate accounts."],
    { type: "text/plain" },
  );
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "BRD Example - Open Backlog.docx";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function mockDownloadBRD(id: number | string, fallbackName: string): Promise<void> {
  const fr = await mockGetFeatureRequest(id);
  if (!fr.brd) throw Object.assign(new Error("brd document not found"), { status: 404 });
  const stored = mockBRDFiles.get(fr.id);
  const blob = stored ?? new Blob([`Mock BRD document: ${fr.brd.file_name}`], { type: fr.brd.content_type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fr.brd.file_name || fallbackName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function mockFetchBRDBlob(id: number | string): Promise<Blob> {
  const fr = await mockGetFeatureRequest(id);
  if (!fr.brd) throw Object.assign(new Error("brd document not found"), { status: 404 });
  const stored = mockBRDFiles.get(fr.id);
  if (!stored) {
    throw Object.assign(new Error("styled preview unavailable in mock without an uploaded file"), {
      status: 404,
    });
  }
  return stored;
}

export async function mockUpdateFeatureRequestStatus(
  id: number | string,
  input: UpdateFeatureRequestStatusInput,
): Promise<FeatureRequest> {
  await delay();
  const user = currentUser();
  if (user.role !== "product" && user.role !== "admin") {
    throw Object.assign(new Error("forbidden"), { status: 403 });
  }

  const idx = requests.findIndex((r) => r.id === Number(id));
  if (idx < 0) {
    throw Object.assign(new Error("feature request not found"), { status: 404 });
  }

  const fr = requests[idx];
  const from = fr.status;
  const to = input.status;
  const decisionStatuses: FeatureRequestStatus[] = ["approved", "rejected", "deferred", "needs_clarification"];
  const allowed =
    (from === "submitted" && (to === "under_review" || decisionStatuses.includes(to))) ||
    (from === "under_review" && decisionStatuses.includes(to)) ||
    ((from === "deferred" ||
      from === "needs_clarification" ||
      from === "approved" ||
      from === "rejected") &&
      to === "under_review");
  if (!allowed) {
    throw Object.assign(new Error("invalid status transition"), { status: 400 });
  }
  if (to === "rejected" && !input.rejection_reason?.trim()) {
    throw Object.assign(new Error("rejection_reason is required"), {
      status: 400,
    });
  }
  if ((to === "deferred" || to === "needs_clarification") && !input.notes?.trim()) {
    throw Object.assign(new Error("notes is required for defer or clarify decisions"), {
      status: 400,
    });
  }
  const now = new Date().toISOString();
  const actor = {
    id: user.id,
    username: user.username,
    display_name: user.display_name,
    role: user.role,
  };

  const updated: FeatureRequest = {
    ...fr,
    status: to,
    product_analysis_notes:
      input.notes?.trim() || fr.product_analysis_notes,
    rejection_reason:
      to === "rejected" ? input.rejection_reason?.trim() || null : fr.rejection_reason,
    decided_at: decisionStatuses.includes(to)
      ? (fr.decided_at ?? now)
      : fr.decided_at,
    reviewed_by_id: user.id,
    reviewed_by: actor,
    reviewed_at: now,
    updated_at: now,
    events: [
      ...(fr.events ?? []),
      // Deciding straight from submitted implies the review step.
      ...(from === "submitted" && decisionStatuses.includes(to)
        ? [
            {
              id: nextEventId++,
              actor_id: user.id,
              actor,
              from_status: "submitted" as FeatureRequestStatus,
              to_status: "under_review" as FeatureRequestStatus,
              note: "Review started",
              created_at: now,
            },
          ]
        : []),
      {
        id: nextEventId++,
        actor_id: user.id,
        actor,
        from_status:
          from === "submitted" && decisionStatuses.includes(to) ? "under_review" : from,
        to_status: to,
        note:
          to === "rejected"
            ? input.rejection_reason?.trim()
            : to === "approved"
              ? input.notes?.trim() || "Proceed"
              : input.notes?.trim(),
        created_at: now,
      },
    ],
  };

  if (to === "approved") {
    // Proceed records the decision + queue seat; Jira create is a separate action.
    updated.queue_position =
      Math.max(0, ...requests.filter((r) => r.queue_position != null).map((r) => r.queue_position ?? 0)) + 1;
  }
  if (from === "approved" && to === "under_review") {
    updated.queue_position = null;
  }
  if (from === "rejected" && to === "under_review") {
    updated.rejection_reason = null;
  }
  if (
    to === "under_review" &&
    (from === "approved" ||
      from === "rejected" ||
      from === "deferred" ||
      from === "needs_clarification") &&
    !input.notes?.trim()
  ) {
    const last = updated.events?.[updated.events.length - 1];
    if (last && !last.note) last.note = "Review reopened";
  }

  requests[idx] = updated;
  return withQueueRanks(requests.map(cloneRequest)).find((r) => r.id === updated.id) ?? cloneRequest(updated);
}

export async function mockListQueue(): Promise<FeatureRequest[]> {
  await delay();
  return withQueueRanks(requests.map(cloneRequest))
    .filter(isQueueActive)
    .sort((a, b) => (a.queue_position ?? 0) - (b.queue_position ?? 0));
}

/** All Proceed rows for the Delivery board (any signed-in role). */
export async function mockListDelivery(): Promise<FeatureRequest[]> {
  await delay();
  const { deliveryHealthOf, deliveryStageOf } = await import("@/lib/delivery-stages");
  return withQueueRanks(requests.map(cloneRequest))
    .filter((r) => r.status === "approved")
    .map((r) => {
      const stage = deliveryStageOf(r);
      const health = deliveryHealthOf(r, stage);
      const updates = r.delivery_updates?.length
        ? r.delivery_updates
        : r.jira_issue_key
          ? [
              {
                id: `mock-${r.id}-1`,
                stage,
                from_status: "In Development",
                to_status: "Ready For QA",
                by_name: "Jira sync (mock)",
                jira_issue_key: r.jira_issue_key,
                note: "In Development → Ready For QA · Jira sync (mock)",
                created_at: new Date().toISOString(),
              },
            ]
          : [];
      return {
        ...r,
        delivery_stage: stage,
        delivery_health: health,
        delivery_updates: updates,
      };
    });
}

export async function mockReorderQueue(orderedIds: number[]): Promise<FeatureRequest[]> {
  await delay();
  const user = currentUser();
  if (user.role !== "product" && user.role !== "admin") {
    throw Object.assign(new Error("forbidden"), { status: 403 });
  }
  const activeIds = requests.filter(isQueueActive).map((r) => r.id);
  const valid =
    orderedIds.length === activeIds.length &&
    new Set(orderedIds).size === orderedIds.length &&
    orderedIds.every((id) => activeIds.includes(id));
  if (!valid) {
    throw Object.assign(new Error("ordered_ids must list every active development-queue request exactly once"), { status: 400 });
  }
  const position = new Map(orderedIds.map((id, i) => [id, i + 1]));
  requests = requests.map((fr) => ({
    ...fr,
    queue_position: position.get(fr.id) ?? (fr.queue_position != null && !position.has(fr.id) ? null : fr.queue_position),
    updated_at: position.has(fr.id) ? new Date().toISOString() : fr.updated_at,
  }));
  return mockListQueue();
}

function toManagedUser(u: User): ManagedUser {
  return {
    id: u.id,
    username: u.username,
    email: u.email,
    display_name: u.display_name,
    role: u.role,
    is_active: u.is_active,
    request_count: requests.filter((r) => r.requester_id === u.id).length,
    created_at: u.created_at ?? new Date().toISOString(),
  };
}

export async function mockListUsers(): Promise<ManagedUser[]> {
  await delay();
  const user = currentUser();
  if (user.role !== "admin") {
    throw Object.assign(new Error("forbidden"), { status: 403 });
  }
  return mockUsers.map(toManagedUser);
}

export async function mockUpdateUserRole(id: number, role: UserRole): Promise<ManagedUser> {
  await delay();
  const actor = currentUser();
  if (actor.role !== "admin") {
    throw Object.assign(new Error("forbidden"), { status: 403 });
  }
  const idx = mockUsers.findIndex((u) => u.id === id);
  if (idx < 0) {
    throw Object.assign(new Error("user not found"), { status: 404 });
  }
  if (actor.id === id) {
    throw Object.assign(new Error("you cannot change your own role"), { status: 400 });
  }
  const target = mockUsers[idx];
  if (target.role === "admin" && role !== "admin" && mockUsers.filter((u) => u.role === "admin").length <= 1) {
    throw Object.assign(new Error("cannot change the role of the last admin"), { status: 400 });
  }
  mockUsers[idx] = { ...target, role };
  return toManagedUser(mockUsers[idx]);
}

export async function mockGetDevelopmentStatus(
  id: number | string,
): Promise<DevelopmentStatus> {
  const fr = await mockGetFeatureRequest(id);
  await delay(120);
  return mockDevelopmentFor(fr);
}

function requireProductActor(): User {
  const user = currentUser();
  if (user.role !== "product" && user.role !== "admin") {
    throw Object.assign(new Error("forbidden"), { status: 403 });
  }
  return user;
}

function asRequestUser(u: User): FeatureRequestUser {
  return {
    id: u.id,
    username: u.username,
    display_name: u.display_name,
    role: u.role,
  };
}

function findMutableRequest(id: number | string): FeatureRequest {
  const fr = requests.find((r) => r.id === Number(id));
  if (!fr) {
    throw Object.assign(new Error("feature request not found"), { status: 404 });
  }
  return fr;
}

export async function mockListReviewers(): Promise<FeatureRequestUser[]> {
  await delay();
  requireProductActor();
  return mockUsers
    .filter((u) => u.is_active && (u.role === "product" || u.role === "admin"))
    .map(asRequestUser)
    .sort((a, b) => a.display_name.localeCompare(b.display_name));
}

export async function mockUpdateCEOHighlight(
  id: number | string,
  ceoHighlight: boolean,
): Promise<FeatureRequest> {
  await delay();
  const actor = requireProductActor();
  const fr = findMutableRequest(id);
  const now = new Date().toISOString();
  Object.assign(fr, {
    ceo_highlight: ceoHighlight,
    ceo_note: ceoHighlight ? fr.ceo_note : null,
    ceo_edited_by_id: actor.id,
    ceo_edited_by: asRequestUser(actor),
    ceo_edited_at: now,
    updated_at: now,
  });
  return mockGetFeatureRequest(id);
}

export async function mockUpdateAdhoc(
  id: number | string,
  adhoc: boolean,
): Promise<FeatureRequest> {
  await delay();
  requireProductActor();
  const fr = findMutableRequest(id);
  fr.adhoc = adhoc;
  fr.updated_at = new Date().toISOString();
  return mockGetFeatureRequest(id);
}

export async function mockUpdatePMInCharge(
  id: number | string,
  pmInChargeId: number | null,
): Promise<FeatureRequest> {
  await delay();
  requireProductActor();
  const fr = findMutableRequest(id);
  if (pmInChargeId == null) {
    fr.pm_in_charge_id = null;
    fr.pm_in_charge = null;
  } else {
    const target = mockUsers.find((u) => u.id === pmInChargeId);
    if (!target?.is_active || (target.role !== "product" && target.role !== "admin")) {
      throw Object.assign(new Error("pm_in_charge_id must be an active product or admin user"), {
        status: 400,
      });
    }
    fr.pm_in_charge_id = target.id;
    fr.pm_in_charge = asRequestUser(target);
  }
  fr.updated_at = new Date().toISOString();
  return mockGetFeatureRequest(id);
}

export async function mockUpdateAgreedDate(
  id: number | string,
  agreedDate: string,
): Promise<FeatureRequest> {
  await delay();
  const actor = requireProductActor();
  const fr = findMutableRequest(id);
  const trimmed = agreedDate.trim();
  const now = new Date().toISOString();
  if (!trimmed) {
    fr.agreed_date = null;
    fr.agreed_by_id = null;
    fr.agreed_by = null;
    fr.agreed_at = null;
  } else {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      throw Object.assign(new Error("expected_deadline must be YYYY-MM-DD"), { status: 400 });
    }
    fr.agreed_date = trimmed;
    fr.agreed_by_id = actor.id;
    fr.agreed_by = asRequestUser(actor);
    fr.agreed_at = now;
  }
  fr.updated_at = now;
  return mockGetFeatureRequest(id);
}

let nextAttachmentId = 1;

export async function mockCreatePMNote(
  id: number | string,
  input: {
    note_date: string;
    text: string;
    attachments?: { object_key: string; file_name: string }[];
  },
): Promise<PMNote> {
  await delay();
  const actor = requireProductActor();
  const fr = findMutableRequest(id);
  const text = input.text.trim();
  const attachmentsIn = input.attachments ?? [];
  if (
    text.length > 2000 ||
    (!text && attachmentsIn.length === 0) ||
    !/^\d{4}-\d{2}-\d{2}$/.test(input.note_date.trim())
  ) {
    throw Object.assign(
      new Error("pm note needs text and/or attachments (text max 2000 characters)"),
      { status: 400 },
    );
  }
  if (attachmentsIn.length > 5) {
    throw Object.assign(new Error("too many pm note attachments"), { status: 400 });
  }
  const noteId = nextNoteId++;
  const note: PMNote = {
    id: noteId,
    note_date: input.note_date.trim(),
    text,
    author_id: actor.id,
    author: asRequestUser(actor),
    created_at: new Date().toISOString(),
    attachments: attachmentsIn.map((a) => {
      const attachmentId = nextAttachmentId++;
      return {
        id: attachmentId,
        file_name: a.file_name,
        file_size: 1024,
        content_type: "application/octet-stream",
        download_url: `/api/feature-requests/${fr.id}/pm-notes/${noteId}/attachments/${attachmentId}`,
      };
    }),
  };
  fr.pm_notes = [note, ...(fr.pm_notes ?? [])];
  fr.updated_at = note.created_at;
  return note;
}

export async function mockUpdatePMNote(
  id: number | string,
  noteId: number,
  input: {
    note_date: string;
    text: string;
    attachments?: { object_key: string; file_name: string }[];
    remove_attachment_ids?: number[];
  },
): Promise<PMNote> {
  await delay();
  requireProductActor();
  const fr = findMutableRequest(id);
  const note = (fr.pm_notes ?? []).find((n) => n.id === noteId);
  if (!note) {
    throw Object.assign(new Error("pm note not found"), { status: 404 });
  }
  const text = input.text.trim();
  const remove = new Set(input.remove_attachment_ids ?? []);
  let attachments = (note.attachments ?? []).filter((a) => !remove.has(a.id));
  for (const a of input.attachments ?? []) {
    const attachmentId = nextAttachmentId++;
    attachments = [
      ...attachments,
      {
        id: attachmentId,
        file_name: a.file_name,
        file_size: 1024,
        content_type: "application/octet-stream",
        download_url: `/api/feature-requests/${fr.id}/pm-notes/${noteId}/attachments/${attachmentId}`,
      },
    ];
  }
  if (attachments.length > 5 || text.length > 2000 || (!text && attachments.length === 0) || !/^\d{4}-\d{2}-\d{2}$/.test(input.note_date.trim())) {
    throw Object.assign(
      new Error("pm note needs text and/or attachments (text max 2000 characters)"),
      { status: 400 },
    );
  }
  note.note_date = input.note_date.trim();
  note.text = text;
  note.attachments = attachments;
  fr.updated_at = new Date().toISOString();
  return { ...note, attachments: [...attachments] };
}

export async function mockDeletePMNote(id: number | string, noteId: number): Promise<void> {
  await delay();
  requireProductActor();
  const fr = findMutableRequest(id);
  const before = fr.pm_notes?.length ?? 0;
  fr.pm_notes = (fr.pm_notes ?? []).filter((n) => n.id !== noteId);
  if ((fr.pm_notes?.length ?? 0) === before) {
    throw Object.assign(new Error("pm note not found"), { status: 404 });
  }
  fr.updated_at = new Date().toISOString();
}

let nextClarificationId = 1;

/** Owning stakeholder (or Product) replies while Clarify; reopens to under_review. */
export async function mockCreateClarification(
  id: number | string,
  input: {
    text: string;
    attachments?: { object_key: string; file_name: string }[];
  },
): Promise<FeatureRequest> {
  await delay();
  const user = currentUser();
  const fr = findMutableRequest(id);
  if (fr.status !== "needs_clarification") {
    throw Object.assign(new Error("clarification replies are only allowed while status is Clarify"), {
      status: 400,
    });
  }
  if (fr.requester_id !== user.id) {
    throw Object.assign(new Error("forbidden"), { status: 403 });
  }
  const text = input.text.trim();
  const attachmentsIn = input.attachments ?? [];
  if (text.length > 2000 || (!text && attachmentsIn.length === 0) || attachmentsIn.length > 5) {
    throw Object.assign(new Error("clarification needs text and/or attachments"), { status: 400 });
  }
  const clarId = nextClarificationId++;
  const now = new Date().toISOString();
  const clar = {
    id: clarId,
    text,
    author_id: user.id,
    author: asRequestUser(user),
    created_at: now,
    attachments: attachmentsIn.map((a) => {
      const attachmentId = nextAttachmentId++;
      return {
        id: attachmentId,
        file_name: a.file_name,
        file_size: 1024,
        content_type: "application/octet-stream",
        download_url: `/api/feature-requests/${fr.id}/clarifications/${clarId}/attachments/${attachmentId}`,
      };
    }),
  };
  fr.clarifications = [clar, ...(fr.clarifications ?? [])];
  const from = fr.status;
  fr.status = "under_review";
  fr.updated_at = now;
  const eventNote =
    text ||
    (attachmentsIn.length === 1
      ? "Clarification submitted with 1 file"
      : attachmentsIn.length > 1
        ? `Clarification submitted with ${attachmentsIn.length} files`
        : "Clarification submitted");
  fr.events = [
    ...(fr.events ?? []),
    {
      id: nextEventId++,
      actor_id: user.id,
      actor: asRequestUser(user),
      from_status: from,
      to_status: "under_review",
      note: eventNote,
      created_at: now,
    },
  ];
  return cloneRequest(fr);
}

/** Reset seed data (handy from console while reviewing UI). */
export function resetMockStore(): void {
  requests = createSeedRequests();
  mockUsers = createSeedUsers();
  nextRequestId = Math.max(...requests.map((r) => r.id), 0) + 1;
  nextEventId =
    Math.max(
      0,
      ...requests.flatMap((r) => (r.events ?? []).map((e) => e.id)),
    ) + 1;
  nextNoteId =
    Math.max(
      0,
      ...requests.flatMap((r) => (r.pm_notes ?? []).map((n) => n.id)),
    ) + 1;
}

if (typeof window !== "undefined") {
  (window as unknown as { __lioQuestResetMock?: () => void }).__lioQuestResetMock =
    resetMockStore;
}
